import {
    Button,
    FormControl,
    FormLabel,
    HStack,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Textarea,
    useColorModeValue,
} from '@chakra-ui/react';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { AbsenceStatus } from '../../types';
import { useReviewAbsence } from '../../query/resource-hooks/absence';
import { useSuToast } from '../../hooks/useSuToast';

type AbsenceReviewModalProps = {
    isOpen: boolean;
    onClose: () => void;
    absenceId: number;
    onSuccess?: () => void;
};

export const AbsenceReviewModal: React.FC<AbsenceReviewModalProps> = ({ isOpen, onClose, absenceId, onSuccess }) => {
    const intl = useIntl();
    const toast = useSuToast();
    const [reviewNote, setReviewNote] = useState('');

    const reviewAbsence = useReviewAbsence(
        () => {
            toast.success(intl.formatMessage({ id: 'absence.reviewed' }));
            setReviewNote('');
            onSuccess?.();
            onClose();
        },
        () => {
            toast.error(intl.formatMessage({ id: 'absence.error.review' }));
        }
    );

    function handleClose() {
        setReviewNote('');
        onClose();
    }

    function handleReview(status: AbsenceStatus.APPROVED | AbsenceStatus.REJECTED) {
        reviewAbsence.mutate({
            id: absenceId,
            data: {
                status,
                reviewNote: reviewNote || undefined,
            },
        });
    }

    return (
        <Modal isOpen={isOpen} onClose={handleClose}>
            <ModalOverlay />
            <ModalContent borderRadius={'xl'}>
                <ModalHeader>{intl.formatMessage({ id: 'absence.review' })}</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    <FormControl>
                        <FormLabel>{intl.formatMessage({ id: 'absence.reviewNote' })}</FormLabel>
                        <Textarea
                            backgroundColor={useColorModeValue('gray.100', 'gray.800')}
                            value={reviewNote}
                            onChange={(e) => setReviewNote(e.target.value)}
                            placeholder={intl.formatMessage({ id: 'absence.reviewNote.placeholder' })}
                        />
                    </FormControl>
                </ModalBody>
                <ModalFooter>
                    <HStack spacing={2}>
                        <Button
                            colorScheme="green"
                            isLoading={reviewAbsence.isLoading}
                            onClick={() => handleReview(AbsenceStatus.APPROVED)}
                        >
                            {intl.formatMessage({ id: 'absence.approve' })}
                        </Button>
                        <Button colorScheme="red" isLoading={reviewAbsence.isLoading} onClick={() => handleReview(AbsenceStatus.REJECTED)}>
                            {intl.formatMessage({ id: 'absence.reject' })}
                        </Button>
                    </HStack>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};
